import { useState } from 'react';
import type { ColumnDef, PaginationState } from '@tanstack/react-table';
import { format, parseISO } from 'date-fns';
import { DataTable } from '@/components/ui/data-table/data-table';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useDrilldown } from '@/hooks/use-analytics-v2';
import { cn } from '@/lib/utils';
import type { DrilldownRequest } from '@/types/analyticsV2';

const PAGE_SIZES = [25, 50, 100];

/** The rows behind one number: what to list, the panel's title, and the path to the number. */
export interface DrilldownView {
  request: DrilldownRequest;
  title: string;
  scope: string[];
}

interface DrilldownBodyProps<Row> {
  view: DrilldownView;
  columns: ColumnDef<Row>[];
  /** What a row is, for the count and the empty state, e.g. "tickets". */
  noun: string;
  fromDate: string;
  toDate: string;
  overrideAccountId?: number;
  /** The column of the metric the panel was opened from, lit top to bottom. */
  markedColumnId?: string;
  /** Opens a row, e.g. a ticket's conversation; rows `canOpenRow` refuses stay inert. */
  onOpenRow?: (row: Row) => void;
  canOpenRow?: (row: Row) => boolean;
}

interface DrilldownPanelProps<Row> extends Omit<DrilldownBodyProps<Row>, 'view'> {
  view: DrilldownView | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const day = (date: string) => format(parseISO(date), 'd MMM yyyy');

/** The rows behind a number in a sheet on the right, a page at a time, in the columns the caller passes. */
export function DrilldownPanel<Row>({ view, open, onOpenChange, ...body }: DrilldownPanelProps<Row>) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side='right' className='w-full gap-0 sm:max-w-6xl'>
        {view ? <DrilldownBody key={JSON.stringify(view.request)} view={view} {...body} /> : null}
      </SheetContent>
    </Sheet>
  );
}

function DrilldownBody<Row>({
  view,
  columns,
  noun,
  fromDate,
  toDate,
  overrideAccountId,
  markedColumnId,
  onOpenRow,
  canOpenRow = () => true,
}: DrilldownBodyProps<Row>) {
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: PAGE_SIZES[0] });
  const { data, isLoading, isFetching, isError } = useDrilldown<Row>(view.request, fromDate, toDate, pagination, overrideAccountId);
  // The pager shows the page on screen, so it stays on the last loaded page while the next one loads or if it fails.
  const shownPagination = data ? { pageIndex: data.page - 1, pageSize: data.pageSize } : pagination;
  const pageFailed = isError && data !== undefined;

  return (
    <>
      <SheetHeader className='gap-1 border-b border-gray-200 px-6 py-3 pr-12'>
        <SheetTitle className='text-base text-ink'>{view.title}</SheetTitle>
        <div className='flex flex-wrap items-center gap-2'>
          <SheetDescription className='text-[12px] text-gray-700'>
            {day(fromDate)} – {day(toDate)}
          </SheetDescription>
          {view.scope.map((part, index) => (
            <span key={index} className='rounded-full bg-gray-100 px-2 py-0.5 text-[12px] text-gray-800'>
              {part}
            </span>
          ))}
        </div>
      </SheetHeader>
      <div className='flex min-h-0 flex-1 flex-col px-6 py-3'>
        {pageFailed ? (
          <p className='mb-2 text-[12px] text-red-700'>Page {pagination.pageIndex + 1} could not be loaded.</p>
        ) : null}
        <DataTable
          columns={columns}
          data={data?.rows ?? []}
          enableSorting={false}
          manualPagination
          rowCount={data?.total ?? 0}
          pagination={shownPagination}
          onPaginationChange={setPagination}
          pageSizeOptions={PAGE_SIZES}
          paginationSummary={data ? `${data.total.toLocaleString()} ${noun}` : null}
          scrollBody
          markedColumnId={markedColumnId}
          onRowClick={onOpenRow && ((row: Row) => (canOpenRow(row) ? onOpenRow(row) : undefined))}
          getRowClassName={onOpenRow && ((row: Row) => (canOpenRow(row) ? '' : 'cursor-default'))}
          isLoading={isLoading}
          className={cn('flex-1 transition-opacity', isFetching && !isLoading && 'opacity-60')}
          emptyMessage={isError ? `Failed to load the ${noun}.` : `No ${noun} behind this number.`}
        />
      </div>
    </>
  );
}
